import { apiFetch } from './client';

export type PosMenuVariant = {
    id: number | null;
    name: string | null;
    price: number | null;
    available: boolean;
    position: number | null;
};

export type PosMenuItem = {
    id: number | null;
    name: string | null;
    description: string | null;
    price: number | null;
    counterPrice: number | null;
    imageUrl: string | null;
    categoryId: number | null;
    subcategoryId: number | null;
    variants?: PosMenuVariant[] | null;
};

export type PosMenuCategory = {
    id: number | null;
    name: string | null;
    description: string | null;
    items: PosMenuItem[];
};

export type PosOrderItem = {
    id: string | null;
    name: string | null;
    quantity: number | null;
    price: number | null;
    total: number | null;
    modifiers: string[];
    category: string | null;
    station: string | null;
    comment?: string | null;
};

export type PosOrderPreview = {
    id: number;
    code: string | null;
    tableNumber: string | null;
    status: string | null;
    paid: boolean;
    total: number | null;
    itemsCount: number | null;
    customerName: string | null;
    openedAt: string | null;
    itemsPreview: PosOrderItem[];
};

export type PosTable = {
    tableNumber: string | null;
    status: string | null;
    occupied: boolean;
    currentOrder: PosOrderPreview | null;
};

export type PosDashboard = {
    tables: PosTable[];
    activeOrders: PosOrderPreview[];
    menu: PosMenuCategory[];
    todaysTotal: number | null;
    openOrders: number | null;
    generatedAt: string | null;
};

export type PosOrderDetail = {
    id: number;
    code: string | null;
    tableNumber: string | null;
    status: string | null;
    paid: boolean;
    total: number | null;
    customerName: string | null;
    openedAt: string | null;
    items: PosOrderItem[];
};

export type PosOrderInvoiceLink = {
    orderId: number | null;
    tenantId: number | null;
    companyId: number | null;
    invoiceStatus: string | null;
    fiscalSnapshot: Record<string, unknown> | null;
    sdiIdentifier: string | null;
    documentReference: string | null;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    emittedAt: string | null;
    sentAt: string | null;
    providerMessage: string | null;
    createdAt: string | null;
    updatedAt: string | null;
};

export async function fetchPosDashboard(): Promise<PosDashboard> {
    const response = await apiFetch('/api/tenant/pos/dashboard');
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile caricare il dashboard POS');
    }
    return response.json();
}

export async function fetchPosOrderDetail(orderId: number): Promise<PosOrderDetail> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}`);
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile caricare il dettaglio ordine POS');
    }
    return response.json();
}

export type PosOrderUpdatePayload = {
    adjustments?: Array<{
        reference?: string | null;
        name?: string | null;
        quantity: number;
    }>;
    additions?: Array<{
        reference?: string | null;
        name?: string | null;
        menuItemId?: number | null;
        price: number;
        quantity: number;
        custom?: boolean;
    }>;
};

export type PosOrderPaymentPayload = PosOrderUpdatePayload & {
    amountDue?: number;
    amountReceived?: number;
    discountAmount?: number;
    voucherAmount?: number;
    changeAmount?: number;
    outstandingAmount?: number;
    method: 'CASH' | 'POS' | 'CUSTOM';
    note?: string;
};

export type CreatePosOrderPayload = {
    tableNumber: string;
    customerName?: string;
    items: Array<{
        reference?: string | null;
        name: string;
        menuItemId?: number | null;
        price: number;
        quantity: number;
        custom?: boolean;
        comment?: string | null;
    }>;
};

const sanitizeItemComment = (value?: string | null): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

export async function createPosOrder(payload: CreatePosOrderPayload): Promise<PosOrderDetail> {
    // Serializza gli items nel formato che il backend si aspetta
    const normalizedItems = payload.items.map(item => ({
        ...item,
        comment: sanitizeItemComment(item.comment),
    }));
    const orderItems = JSON.stringify({ items: normalizedItems });
    const totalAmount = normalizedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const response = await apiFetch('/api/public/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            tableNumber: payload.tableNumber,
            customerName: payload.customerName || null,
            status: 'OPEN',
            orderItems,
            totalAmount,
            isPaid: false,
        }),
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile creare l\'ordine');
    }

    const data = await response.json();

    // Converti la risposta al formato PosOrderDetail
    return {
        id: data.id,
        code: data.orderCode,
        tableNumber: data.tableNumber,
        status: data.status || 'OPEN',
        paid: Boolean(data.isPaid),
        total: data.totalAmount || totalAmount,
        customerName: data.customerName,
        openedAt: null,
        items: normalizedItems.map(item => ({
            id: item.reference || item.name,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            total: item.price * item.quantity,
            modifiers: [],
            category: null,
            station: null,
            comment: item.comment ?? null,
        })),
    };
}

/**
 * Aggiunge items a un ordine esistente
 */
export async function addItemsToPosOrder(orderCode: string, newItems: CreatePosOrderPayload['items']): Promise<void> {
    // Prima otteniamo l'ordine esistente
    const response = await apiFetch(`/api/public/orders/${orderCode}`);
    if (!response.ok) {
        throw new Error('Impossibile recuperare l\'ordine');
    }

    const existingOrder = await response.json();

    // Parse existing items
    let existingItems = [];
    try {
        const parsed = JSON.parse(existingOrder.orderItems);
        existingItems = parsed.items || [];
    } catch {
        existingItems = [];
    }

    // Merge new items with existing
    const sanitizedNewItems = newItems.map(item => ({
        ...item,
        comment: sanitizeItemComment(item.comment),
    }));

    const allItems = [...existingItems, ...sanitizedNewItems];
    const updatedOrderItems = JSON.stringify({ items: allItems });
    const newTotalAmount = allItems.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);

    // Update order - Invia SOLO i campi necessari senza l'oggetto tenant
    const updatePayload = {
        id: existingOrder.id,
        orderCode: existingOrder.orderCode,
        tableNumber: existingOrder.tableNumber,
        customerName: existingOrder.customerName,
        status: existingOrder.status,
        isPaid: existingOrder.isPaid,
        paymentMethod: existingOrder.paymentMethod,
        orderItems: updatedOrderItems,
        totalAmount: newTotalAmount,
        createdAt: existingOrder.createdAt,
        // NON includiamo tenant per evitare campi calcolati come supportFreeRemaining
    };

    const updateResponse = await apiFetch(`/api/public/orders/${orderCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
    });

    if (!updateResponse.ok) {
        const text = await updateResponse.text();
        throw new Error(text || 'Impossibile aggiungere prodotti all\'ordine');
    }
}

export async function updatePosOrder(orderId: number, payload: PosOrderUpdatePayload): Promise<PosOrderDetail> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ?? {}),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile aggiornare l\'ordine POS');
    }
    return response.json();
}

export async function cancelPosOrder(orderId: number, payload?: PosOrderUpdatePayload): Promise<PosOrderDetail> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ?? {}),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile annullare l\'ordine POS');
    }
    return response.json();
}

export async function registerPosOrderPayment(orderId: number, payload: PosOrderPaymentPayload): Promise<PosOrderDetail> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ?? {}),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile registrare il pagamento POS');
    }
    return response.json();
}

export async function fetchPosOrderInvoice(orderId: number): Promise<PosOrderInvoiceLink | null> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}/invoice`);
    if (response.status === 404) {
        return null;
    }
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile recuperare la fattura collegata all\'ordine POS');
    }
    return response.json();
}

export async function linkPosOrderCompany(orderId: number, companyId: number): Promise<PosOrderInvoiceLink> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}/invoice/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId }),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile collegare l\'azienda all\'ordine POS');
    }
    return response.json();
}

export async function issuePosOrderInvoice(orderId: number): Promise<PosOrderInvoiceLink> {
    const response = await apiFetch(`/api/tenant/pos/orders/${orderId}/invoice/issue`, {
        method: 'POST',
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile inviare la fattura per l\'ordine POS');
    }
    return response.json();
}

export async function deletePosTable(tableNumber: string): Promise<void> {
    const response = await apiFetch(`/api/tenant/pos/tables/${encodeURIComponent(tableNumber)}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Impossibile eliminare il tavolo');
    }
}
